import { cliente } from '@/lib/cliente';
import type {
  ActualizarUmbralRequest,
  ListaUmbralesResponse,
  RespuestaApi,
  UmbralAlerta,
  UmbralesCumplimiento,
} from '@adelante/shared';
import { UMBRALES_CUMPLIMIENTO_DEFAULT } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

/**
 * Umbrales de alerta parametrizables.
 *
 * Catálogo chico (~14 filas), cambia con poca frecuencia → cache largo
 * (`staleTime: 10 min`) para no refrescar en cada render de la tabla de
 * batches o de muestras.
 */
export function useUmbrales() {
  return useQuery<RespuestaApi<ListaUmbralesResponse>>({
    queryKey: ['umbrales-alerta'],
    queryFn: () => cliente.umbrales.listar(),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Devuelve un Map<clave, UmbralAlerta> para lookup O(1) desde los componentes
 * que evalúan alertas por celda (TablaBatchesDetalle).
 */
export function useUmbralesMap(): Map<string, UmbralAlerta> {
  const q = useUmbrales();
  const lista = q.data?.data?.umbrales ?? [];
  return useMemo(() => {
    const m = new Map<string, UmbralAlerta>();
    for (const u of lista) m.set(u.clave, u);
    return m;
  }, [lista]);
}

/**
 * Devuelve los umbrales de cumplimiento de laboratorio. Si la query aún
 * no cargó o el admin desactivó alguna entrada, cae al default
 * (cumple 0.95 / marginal 0.85). Lo usan `MuestraDetallePantalla` y
 * `InformeMuestraPantalla` para que la evaluación live en el frontend
 * coincida con la del backend.
 */
export function useUmbralesCumplimiento(): UmbralesCumplimiento {
  const q = useUmbrales();
  const lista = q.data?.data?.umbrales ?? [];
  return useMemo(() => {
    let cumple = UMBRALES_CUMPLIMIENTO_DEFAULT.cumple;
    let marginal = UMBRALES_CUMPLIMIENTO_DEFAULT.marginal;
    for (const u of lista) {
      if (!u.activo) continue;
      if (u.clave === 'lab.umbral_cumple') cumple = u.umbral;
      else if (u.clave === 'lab.umbral_marginal') marginal = u.umbral;
    }
    return { cumple, marginal };
  }, [lista]);
}

export function useActualizarUmbral() {
  const qc = useQueryClient();
  return useMutation<
    RespuestaApi<UmbralAlerta>,
    Error,
    { clave: string } & ActualizarUmbralRequest
  >({
    mutationFn: ({ clave, ...body }) => cliente.umbrales.actualizar(clave, body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success('Umbral actualizado');
        qc.invalidateQueries({ queryKey: ['umbrales-alerta'] });
        // También refresca el listado de muestras: el cumplimiento se
        // recalcula server-side con los nuevos umbrales lab.*.
        qc.invalidateQueries({ queryKey: ['lab', 'muestras'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo actualizar el umbral', { description: e.message });
    },
  });
}
